import { useMemo, useRef } from 'react'
import { useExperimentContext } from '@/experimentation'
import { ShowtimeReleaseType } from '@/types/codegen-federation'
import { useTicketsContext } from '../components/TicketsContext'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'

const getVenuesSortedByDistance = (venues: ShowtimesVenue[]) => {
  return venues.toSorted((a, b) => {
    if (a.distance === undefined) return 1
    if (b.distance === undefined) return -1
    return a.distance - b.distance
  })
}

const useSortedVenues = ({
  venues,
  releaseTypeFilter,
}: {
  venues: ShowtimesVenue[]
  releaseTypeFilter: ShowtimeReleaseType[]
}) => {
  const { preferredVenueId } = useTicketsContext()
  const { getFeatureValue } = useExperimentContext()

  // Save a ref of the initial preferred venue to avoid re-sorting when user changes selection
  const initialPreferredVenueIdRef = useRef<string | null>(preferredVenueId)

  const venuesWithShowtimes = useMemo(() => {
    const sorted = getVenuesSortedByDistance(
      venues
        ?.map((venue) => ({
          ...venue,
          showtimes: venue?.showtimes.filter(
            (showtime) => releaseTypeFilter.length === 0 || releaseTypeFilter.includes(showtime.releaseType),
          ),
        }))
        .filter((venue) => venue?.showtimes.length > 0) ?? [],
    )
    const preferredIdx = sorted.findIndex((venue) => venue.providerId === initialPreferredVenueIdRef.current)
    const preferredVenue = sorted[preferredIdx]

    if (preferredVenue && getFeatureValue('sort-preferred-venue-first', false)) {
      return [preferredVenue, ...sorted.filter((_, idx) => idx !== preferredIdx)]
    }

    return sorted
  }, [venues, releaseTypeFilter, getFeatureValue])

  const venuesWithNoShowtimes = useMemo(() => {
    const filtered = venues?.filter((venue) => {
      if (releaseTypeFilter.length > 0) {
        return venue?.showtimes.every((showtime) => !releaseTypeFilter.includes(showtime.releaseType))
      }
      return venue?.showtimes.length === 0
    })
    return getVenuesSortedByDistance(filtered)
  }, [venues, releaseTypeFilter])

  return { venuesWithShowtimes, venuesWithNoShowtimes }
}

export default useSortedVenues
